import { PUBLIC_POSTHOG_HOST, PUBLIC_POSTHOG_KEY } from "@/config";
import type { PostHog } from "posthog-js";

/**
 * The PostHog bootstrap shared by both anonymous public surfaces: the Show funnel (ADR-0061) and
 * the shop browse funnel (ADR-0076).
 *
 * Shared rather than copied because ADR-0061's location-signal suppression is the part of its
 * privacy property that lives in code, and a second copy is how one surface would quietly lose it.
 * Everything below applies to every anonymous event this app sends, whichever page sent it;
 * per-surface differences (identity, persistence, vocabulary) stay in the callers.
 */

let posthogPromise: Promise<PostHog> | undefined;

declare global {
  interface Window {
    troviePosthog?: PostHog;
  }
}

/** `$geoip_disable` is PostHog's documented per-event opt-out from location enrichment, and it is
 * the only part of ADR-0061's "no address is kept" property that code can deliver. A null `$ip`
 * does not achieve it: the enricher reads `event.properties.$ip || event.ip`, so a falsy value
 * falls through to the connection address the capture endpoint records server-side. The null only
 * stops a client-supplied address being forwarded. Keeping the connection address from being
 * stored needs the project's "Discard client IP data" setting, which no code here can enforce;
 * `.github/workflows/posthog-privacy-watch.yml` watches it instead (TRO-631). */
export function withoutLocationSignals(properties: Record<string, unknown> | undefined) {
  return { ...properties, $geoip_disable: true, $ip: null };
}

/** Strips the `q` parameter from any absolute URL in a property. Show search text travels in the
 * query string, and PostHog's automatic `$current_url` and `$referrer` would otherwise carry a
 * visitor's typed query into storage, which ADR-0061 forbids. Applied on both surfaces because
 * the shop page has a search box too. */
export function removeSearchText(properties: Record<string, unknown> | undefined) {
  if (!properties) return;
  for (const [name, value] of Object.entries(properties)) {
    if (typeof value !== "string") continue;
    try {
      const url = new URL(value);
      url.searchParams.delete("q");
      properties[name] = url.toString();
    } catch {
      // PostHog URL properties are normally absolute; leave malformed third-party referrers alone.
    }
  }
}

/** Loads the slim PostHog build from its own script, kept out of the Expo bundle so the public
 * pages paint before any analytics code is fetched. `browse-deploy.yml` asserts that placement by
 * grepping the built output. */
export function loadPostHog(): Promise<PostHog> {
  if (window.troviePosthog) return Promise.resolve(window.troviePosthog);
  if (posthogPromise) return posthogPromise;

  posthogPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "module";
    script.src = "/posthog-loader.js";
    script.onload = () =>
      window.troviePosthog
        ? resolve(window.troviePosthog)
        : reject(new Error("PostHog failed to initialize"));
    script.onerror = () => reject(new Error("PostHog failed to load"));
    document.head.append(script);
  });
  return posthogPromise;
}

type AnonymousConfig = {
  bootstrap: { distinctID: string; };
  persistence: "memory";
  [option: string]: unknown;
};

/**
 * Initialises the anonymous client with the options every public surface shares, and returns it
 * with its distinct id pinned to the caller's.
 *
 * The `reset()` is required: `posthog.init` returns the existing instance when one has already
 * been created for this key, so a visitor who reaches a shop page after a Show page would
 * otherwise keep the Show's distinct id, the join ADR-0076 forbids.
 */
export async function initAnonymousPostHog(config: AnonymousConfig): Promise<PostHog> {
  const posthog = await loadPostHog();
  const instance = posthog.init(PUBLIC_POSTHOG_KEY, {
    ...config,
    advanced_disable_feature_flags: true,
    api_host: PUBLIC_POSTHOG_HOST,
    before_send(event) {
      if (!event) return event;
      removeSearchText(event.properties);
      removeSearchText(event.$set);
      removeSearchText(event.$set_once);
      return { ...event, properties: withoutLocationSignals(event.properties) };
    },
    disable_conversations: true,
    disable_product_tours: true,
    disable_surveys: true,
  });

  if (instance.get_distinct_id() !== config.bootstrap.distinctID) {
    instance.reset({
      bootstrap: { distinctID: config.bootstrap.distinctID, isIdentifiedID: false },
    });
  }

  return instance;
}
